import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Subscription } from 'rxjs';
import { MediaMatcher } from '@angular/cdk/layout';
import { LayerService } from 'src/app/features/map-features/sections/locations-section/services/Layers.service';
import { MapHelperService } from 'src/services/map-helper.service';

@Component({
  selector: 'annotation-layer-general-management',
  templateUrl: './annotation-layer-general-management.component.html',
  styleUrls: ['./annotation-layer-general-management.component.css'],
  animations: [
    trigger('searcherPanel', [
      state('open', style({ transform: 'translateX(0)', opacity: 1 })),
      state('closed', style({ transform: 'translateX(100%)', opacity: 0 })),
      transition('open <=> closed', animate('300ms ease-in-out')),
    ]),
  ],
})
export class AnnotationLayerGeneralManagementComponent implements OnInit, OnDestroy {
  sources: {name: string, displayName: string, type: string, order: number, visible: boolean, layerIds: string[], layerCount: number, hasData: boolean, icon: string, displayAttribute: string, canBeSearched: boolean }[] = [];
  visibleSources: {name: string, displayName: string, type: string, order: number, visible: boolean, layerIds: string[], layerCount: number, hasData: boolean, icon: string, displayAttribute: string, canBeSearched: boolean }[] = [];
  
  isMinimized: boolean = false;

  isSearcherPanelOpen: boolean = false;
  selectedSearchSource: {name: string, displayName: string, displayAttribute: string} | null = null;
  searcherTotalResults: string = 'Cargando...';
  
  private mainSourceSubscription: Subscription;
  private sourcesSubscription: Subscription;
  private layersSubscription: Subscription;

  private isManualReordering: boolean = false;

  mobileQuery: MediaQueryList;
  _mobileQueryListener: () => void;

  constructor(
    private mapHelper: MapHelperService,
    public layerCatalogService: LayerService,
    changeDetectorRef: ChangeDetectorRef,
    media: MediaMatcher
  ) {
    this.mobileQuery = media.matchMedia("(min-width: 769px)");
    this._mobileQueryListener = () => {
      if (!this.mobileQuery.matches) {
        this.isMinimized = true;
      }
      changeDetectorRef.detectChanges();
    };
    this.mobileQuery.addListener(this._mobileQueryListener);


  }

  ngOnInit(): void {
    this.mainSourceSubscription = this.mapHelper.mainSource$.subscribe(source => {
      if (source && source.isMainSourceReady && source.isStyleReady) {
        this.startListeningToSourcesAndLayers();
      }
    });

    if(!this.mobileQuery.matches){
      this.isMinimized = true;
    }
  }

  ngOnDestroy(): void {
    if (this.mainSourceSubscription) {
      this.mainSourceSubscription.unsubscribe();
    }
    if (this.sourcesSubscription) {
      this.sourcesSubscription.unsubscribe();
    }
    if (this.layersSubscription) {
      this.layersSubscription.unsubscribe();
    }
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
  }

  startListeningToSourcesAndLayers(): void {
    if (this.sourcesSubscription) {
      this.sourcesSubscription.unsubscribe();
    }
    
    this.sourcesSubscription = this.mapHelper.registeredSources.subscribe(registeredSources => {
      if (registeredSources) {
        this.updateSourcesList(registeredSources);
      }
    });

    if (this.layersSubscription) {
      this.layersSubscription.unsubscribe();
    }

    this.layersSubscription = this.mapHelper.registeredLayers.subscribe(registeredLayers => {
      if (registeredLayers) {
        this.updateLayersInSources(registeredLayers);
      }
    });
  }

  updateSourcesList(registeredSources: any[]): void {
    if (this.isManualReordering) {
      return;
    }

    const currentSourceNames = this.sources.map(s => s.name);
    const newSourceNames = registeredSources.map(s => s.name);

    //agrega nuevos sources
    registeredSources.forEach(source => {
      if (!currentSourceNames.includes(source.name)) {
        const registeredLayers = this.mapHelper.getRegisteredLayers();
        const layersForSource = registeredLayers.filter(layer => layer.sourceName === source.name);
        
        if (layersForSource.length > 0) {
          const allVisible = layersForSource.every(layer => this.isLayerVisible(layer.id));
          
          this.sources.push({
            name: source.name,
            displayName: source.sourceDisplayName ,
            type: source.type,
            order: source.order,
            visible: allVisible,
            layerIds: layersForSource.map(l => l.id),
            layerCount: layersForSource.length,
            hasData: source.hasData ?? false,
            icon: source.icon ?? '',
            displayAttribute: source.displayAttribute ?? '',
            canBeSearched: source.canBeSearched ?? false
          });
        }
      } else {
        //si no se actualiza el hasData, se muere
        const existingSource = this.sources.find(s => s.name === source.name);
        if (existingSource) {
          existingSource.hasData = source.hasData ?? false;
          existingSource.canBeSearched = source.canBeSearched ?? false;
          existingSource.order = source.order;
        }
      }
    });

    this.sources = this.sources.filter(source => newSourceNames.includes(source.name));
    this.sources.sort((a, b) => b.order - a.order);
    this.visibleSources = this.sources.filter(s => s.hasData);
  }

  updateLayersInSources(registeredLayers: any[]): void {
    const registeredSources = this.mapHelper.getRegisteredSources();
    
    this.sources.forEach(source => {
      const layersForSource = registeredLayers.filter(layer => layer.sourceName === source.name);
      const registeredSource = registeredSources.find(rs => rs.name === source.name);
      
      if (layersForSource.length > 0) {
        const allVisible = layersForSource.every(layer => this.isLayerVisible(layer.id));
        
        source.layerIds = layersForSource.map(layer => layer.id);
        source.layerCount = layersForSource.length;
        source.visible = allVisible;
      } else {
        source.layerCount = 0;
      }
      
      if (registeredSource) {
        source.hasData = registeredSource.hasData;
      }
    });
    
    this.sources = this.sources.filter(source => source.layerCount > 0);
    this.visibleSources = this.sources.filter(s => s.hasData);
  }

  loadSourcesFromData(registeredSources: any[], registeredLayers: any[]): void {
    //para que no se dupliquen
    const sourceMap = new Map<string, any>();
    
    registeredSources.forEach(source => {
      const layersForSource = registeredLayers.filter(layer => layer.sourceName === source.name);
      
      if (layersForSource.length > 0) {
        const allVisible = layersForSource.every(layer => this.isLayerVisible(layer.id));
        
        sourceMap.set(source.name, {
          name: source.name,
          displayName: source.sourceDisplayName,
          type: source.type,
          order: source.order,
          visible: allVisible,
          layerIds: layersForSource.map(l => l.id),
          layerCount: layersForSource.length,
          hasData: source.hasData,
          icon: source.icon ?? '',
          displayAttribute: source.displayAttribute ?? '',
          canBeSearched: source.canBeSearched ?? false
        });
      }
    });
    
    this.sources = Array.from(sourceMap.values()).sort((a, b) => b.order - a.order);
    this.visibleSources = this.sources.filter(s => s.hasData);
  }

  formatSourceName(sourceName: string): string {
    return sourceName.replace('-source', '').replace('-highsource', '').replace('polygon', '').replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  isLayerVisible(layerId: string): boolean {
    const layer = this.mapHelper.GetLayerByName(layerId);
    if (!layer) 
      return false;
    
    const visibility = this.mapHelper._map.getLayoutProperty(layerId, 'visibility');
    return visibility !== 'none';
  }

  onToggleSourceVisibility(source: any): void {
    if (source.visible) {
      source.layerIds.forEach(layerId => {
        this.mapHelper.HideLayer(layerId);
      });
    } else {
      source.layerIds.forEach(layerId => {
        this.mapHelper.ShowLayer(layerId);
      });
    }
    source.visible = !source.visible;
  }

  onDropSource(event: CdkDragDrop<any[]>): void {
    const previousIndex = event.previousIndex;
    const currentIndex = event.currentIndex;
    
    if (previousIndex === currentIndex) 
      return;
    
    this.isManualReordering = true;

    this.visibleSources = this.sources.filter(s => s.hasData);
    const hiddenSources = this.sources.filter(s => !s.hasData);
    
    moveItemInArray(this.visibleSources, previousIndex, currentIndex);
    this.visibleSources.forEach((source, index) => {
      source.order = this.visibleSources.length - 1 - index;
    });
    
    this.sources = [...this.visibleSources, ...hiddenSources].sort((a, b) => b.order - a.order);

    const allLayerIds: string[] = [];
    const reversedSources = [...this.visibleSources].reverse();
    reversedSources.forEach(source => {
      allLayerIds.push(...source.layerIds);
    });
    this.mapHelper.layerReorganizationFirstIsLast(allLayerIds);

    // Actualizar orden de sources en el helper
    const orderedSourceNames = this.sources.map(s => s.name);
    this.mapHelper.updateSourcesOrder(orderedSourceNames);
    
    setTimeout(() => {
      this.isManualReordering = false;
    }, 100);
  }

  refreshSources(): void {
    const registeredSources = this.mapHelper.getRegisteredSources();
    const registeredLayers = this.mapHelper.getRegisteredLayers();
    this.loadSourcesFromData(registeredSources, registeredLayers);
  }

  openSourceSearcher(source: {name: string, displayName: string, displayAttribute: string}): void {
    if (this.isSearcherPanelOpen && this.selectedSearchSource?.name === source.name) {
      this.closeSourceSearcher();
      return;
    }
    this.searcherTotalResults = 'Cargando...';
    this.selectedSearchSource = source;
    this.isSearcherPanelOpen = true;
  }

  closeSourceSearcher(): void {
    this.isSearcherPanelOpen = false;
    this.selectedSearchSource = null;
    this.searcherTotalResults = 'Cargando...';
  }

  updateSearchTotal(total: string): void {
    this.searcherTotalResults = total;
  }
}
